import React, { useContext, useRef } from "react";
import { RouteComponentProps } from "react-router-dom";
import { Card } from "@cmsgov/ds-medicare-gov";
import {
  ActionType,
  PharmacyType,
  PlanCategoryType,
  SearchResultPlan,
} from "../../@types";
import PlanCardHeader from "./PlanCardHeader";
import PlanCardActions from "./PlanCardActions";
import PlanBenefits from "./PlanBenefits";
import MonthlyPremium from "./MonthlyPremium";
import OtherCosts from "./OtherCosts";
import Drugs from "./Drugs";
import {
  CONSTANTS,
  isMedicareAdvantageType,
  arePlansComparable,
  useCosts,
  useTranslate,
  PreviewContext,
  handleFocus,
} from "../../helpers";
import Pharmacies from "./Pharmacies";
import Deductible from "./Deductible";
import Copays from "./Copays";
import YearlyCost from "./YearlyCost";
import { AppContext } from "../../app/store";
import { CollapsibleBox } from "..";
import routes from "../../app/routes";

interface PlanCardProps extends RouteComponentProps {
  plan: SearchResultPlan;
  handleAddToCompare?: (plan: SearchResultPlan) => void;
  handleRemoveFromCompare?: (removeId: number | string) => void;
  isCurrentPlan?: boolean;
}

const PlanCard: React.FunctionComponent<PlanCardProps> = ({
  plan,
  handleAddToCompare,
  handleRemoveFromCompare,
  isCurrentPlan,
  ...props
}) => {
  const t = useTranslate();
  const {
    state: { beneficiary, language, planType },
    dispatch,
  } = useContext(AppContext);
  const previewMode = useContext(PreviewContext);
  const ref = useRef(null);
  const drugCostInfo = useCosts(plan, ref);
  const hideCompareButton =
    isCurrentPlan && planType && !arePlansComparable(plan.plan_type, planType);

  const { search } = props.location;
  const pdes = beneficiary ? beneficiary.pdes : [];
  const drugSelectionRouteBase =
    pdes && pdes.length > 0 ? routes.pdePage : routes.managePrescriptions;
  const drugSelectionRoute = `${drugSelectionRouteBase}${search}`;
  const compareAnchorTarget = "plan-compare-footer-sticky-tray-heading";

  const onGoToDrugSelection = (): void => {
    dispatch({
      type: ActionType.UPDATE_PHARMACY_TYPE,
      payload: PharmacyType.MAIL_AND_RETAIL,
    });
  };

  return (
    <Card
      className={`PlanCard ${plan.plan_type}${
        previewMode ? " preview-mode" : ""
      }`}
    >
      {isCurrentPlan ? (
        <div className="PlanCard__current_plan_banner">
          {t("plan_card.your_current_plan")}
        </div>
      ) : null}
      <PlanCardHeader plan={plan} />
      {plan.category === PlanCategoryType.MSA && (
        <p>
          <a
            href={CONSTANTS.linkToWhatIsMSA(language)}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("msa_plan.how_does_msa_work")}
          </a>
        </p>
      )}

      <div ref={ref} className="PlanCard__body">
        <div>
          <div className="PlanCard__flex_left">
            <div>
              <MonthlyPremium plan={plan} />
              {!previewMode && (
                <YearlyCost plan={plan} drugCostInfo={drugCostInfo} />
              )}
              {isMedicareAdvantageType(plan.plan_type) ? (
                <OtherCosts plan={plan} />
              ) : (
                <Deductible plan={plan} />
              )}
            </div>
            {!previewMode && (
              <PlanCardActions
                plan={plan}
                isCurrentPlan={isCurrentPlan}
                handleAddToCompare={handleAddToCompare}
                handleRemoveFromCompare={handleRemoveFromCompare}
                hideCompareButton={hideCompareButton}
              />
            )}
          </div>
          <div className="PlanCard__flex_right">
            <CollapsibleBox
              labelId="plan_card.view_more_information"
              containerClassname="PlanCard__more_info"
              titleClassname="PlanCard__more_info_trigger mct-hide-print"
              open={previewMode}
            >
              {isMedicareAdvantageType(plan.plan_type) ? (
                <>
                  <PlanBenefits plan={plan} />
                  <Copays plan={plan} />
                </>
              ) : (
                <Pharmacies
                  plan={plan}
                  drugCostInfo={drugCostInfo}
                  drugSelectionRoute={drugSelectionRoute}
                  onGoToDrugSelection={onGoToDrugSelection}
                />
              )}
              <Drugs
                plan={plan}
                drugSelectionRoute={drugSelectionRoute}
                onGoToDrugSelection={onGoToDrugSelection}
              />
            </CollapsibleBox>
          </div>
        </div>
      </div>
      <a
        className="PlanCard__skip_link"
        href={`#${compareAnchorTarget}`}
        onClick={(e): void => handleFocus(e, compareAnchorTarget)}
      >
        {t("planCard_skip_text")}
      </a>
    </Card>
  );
};

export default PlanCard;
